import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Welcome = () => {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (user) return;
    let cancelled = false;
    axios.get(`${API}/auth/me`)
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch(() => { /* AppShell will handle unauth state */ });
    return () => { cancelled = true; };
  }, [user]);

  const firstName = user?.name ? user.name.split(' ')[0] : null;

  return (
    <AppShell>
      <SEO
        title="Welcome to Summaros"
        description="Welcome. Let's start with you."
        path="/welcome"
        noindex
      />
      <section className="max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-32 text-center animate-fade-up">
        <p className="eyebrow mb-6" data-testid="welcome-eyebrow">
          Welcome to Summaros{firstName ? `, ${firstName}` : ''}
        </p>
        <h1
          className="display-hero text-4xl sm:text-6xl leading-[1.06] mb-6 text-balance"
          data-testid="welcome-title"
          style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 60, 'wght' 340" }}
        >
          Let's start with you.
        </h1>
        <p className="text-[18px] sm:text-[20px] text-ink-muted leading-[1.6] max-w-xl mx-auto mb-12 text-pretty">
          A short, quiet assessment. A few parts of your life,
          a few honest answers. What comes back is a Life Profile
          that's yours.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-16">
          <Link to="/assess" className="btn-primary" data-testid="welcome-start">
            Start my Life Assessment <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
          <Link to="/my-life" className="btn-secondary" data-testid="welcome-look">
            I'll look around first
          </Link>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto text-left">
          {[
            { title: 'Private', body: 'Your assessment is yours alone. Nothing you say here shows up in Community.' },
            { title: 'Short', body: 'A few minutes, one question at a time. Leave whenever, come back later.' },
            { title: 'Honest', body: 'No 1 to 10 scales. Just answers that sound like the way you actually talk.' },
          ].map((c) => (
            <div key={c.title} className="rounded-xl border border-line bg-cream-soft px-5 py-4">
              <p className="text-sm font-medium text-forest mb-1">{c.title}</p>
              <p className="text-[13px] text-ink-muted leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>
    </AppShell>
  );
};

export default Welcome;
